// =========================================================================
// CLIENTE REST DIRECTO DE FIREBASE AUTH (Identity Toolkit v1)
// -------------------------------------------------------------------------
// Por qué existe:
//   `@react-native-firebase/auth` en Android está colgándose en el handshake
//   de Play Integrity (logcat: "Creating user … with empty reCAPTCHA token"
//   y luego silencio absoluto 15 s+). El backend REST de Firebase responde
//   en milisegundos sin pasar por Play Integrity (comprobado con curl),
//   así que mantenemos este fallback para que el signup/login funcione
//   AHORA, mientras se diagnostica la config nativa.
//
// Cómo se usa:
//   `authBridge.signUp / signIn` intenta primero el SDK nativo con timeout
//   8 s; si nativo no responde, llama a este cliente REST y persiste el
//   usuario en `restAuthCache` para que el resto de la app lo lea.
//
// Seguridad:
//   La API key es la `apiKey` pública del proyecto (la misma que va en
//   google-services.json y en cualquier app cliente Firebase). NO es un
//   secreto. Lo único sensible son los tokens del usuario que devolvemos
//   y que almacenamos en AsyncStorage cifrado del device.
// =========================================================================

const API_KEY = 'AIzaSyBzSgMwf4mCy1ot6XeiDKPzCFa9d-v8TuQ';
const ID_BASE = 'https://identitytoolkit.googleapis.com/v1/accounts';
const TOKEN_BASE = 'https://securetoken.googleapis.com/v1/token';

export type RestAuthUser = {
  uid: string;
  email: string;
  emailVerified: boolean;
  idToken: string;
  refreshToken: string;
  expiresAt: number; // epoch ms
};

type FirebaseRestError = {
  error: {
    code: number;
    message: string;
    errors?: { message: string; domain: string; reason: string }[];
  };
};

// Timeout por intento. fetchWithRetry hace 3 intentos con backoff
// exponencial, así que el peor caso end-to-end es 3 * timeout + ~7s de
// backoff. Mantenemos 15s por intento (en vez de los 25s del único shot
// anterior) para que en redes flakeas-pero-vivas el primer intento ya
// haya abortado y el segundo encuentre la ventana sin paquetes perdidos.
const REST_PER_ATTEMPT_TIMEOUT_MS = 15_000;

import { fetchWithRetry } from './fetchWithRetry';

async function post<T>(url: string, body: any): Promise<T> {
  let resp: Response;
  try {
    resp = await fetchWithRetry(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      perAttemptTimeoutMs: REST_PER_ATTEMPT_TIMEOUT_MS,
    });
  } catch (e: any) {
    // fetchWithRetry agotó reintentos por error de red.
    if (e?.name === 'AbortError') {
      const err: any = new Error('REST_SLOW_NETWORK');
      err.code = 'auth/network-slow';
      throw err;
    }
    const err: any = new Error(e?.message || 'network-error');
    err.code = 'auth/network-error';
    throw err;
  }

  const text = await resp.text();
  if (!resp.ok) {
    // Firebase responde con un JSON {error:{message:"CÓDIGO"}} para errores.
    // Normalizamos al mismo shape que devuelve el SDK nativo (code: 'auth/x').
    let parsed: FirebaseRestError | null = null;
    try { parsed = JSON.parse(text); } catch { /* texto plano */ }
    const restCode = parsed?.error?.message ?? text;
    const error: any = new Error(restCode);
    error.code = restCodeToFirebaseCode(restCode);
    error.restCode = restCode;
    throw error;
  }
  return JSON.parse(text) as T;
}

/** Mapea los códigos REST de Identity Toolkit a los códigos del SDK nativo. */
function restCodeToFirebaseCode(restCode: string): string {
  // Identity Toolkit devuelve strings tipo "EMAIL_EXISTS", "INVALID_PASSWORD",
  // "EMAIL_NOT_FOUND", "INVALID_LOGIN_CREDENTIALS", "TOO_MANY_ATTEMPTS_TRY_LATER",
  // "WEAK_PASSWORD : Password should be at least 6 characters", etc.
  if (restCode.startsWith('EMAIL_EXISTS')) return 'auth/email-already-in-use';
  if (restCode.startsWith('INVALID_EMAIL')) return 'auth/invalid-email';
  if (restCode.startsWith('WEAK_PASSWORD')) return 'auth/weak-password';
  if (restCode.startsWith('EMAIL_NOT_FOUND')) return 'auth/user-not-found';
  if (restCode.startsWith('INVALID_PASSWORD')) return 'auth/wrong-password';
  if (restCode.startsWith('INVALID_LOGIN_CREDENTIALS')) return 'auth/invalid-credential';
  if (restCode.startsWith('USER_DISABLED')) return 'auth/user-disabled';
  if (restCode.startsWith('TOO_MANY_ATTEMPTS_TRY_LATER')) return 'auth/too-many-requests';
  if (restCode.startsWith('OPERATION_NOT_ALLOWED')) return 'auth/operation-not-allowed';
  return `auth/${restCode.toLowerCase().replace(/[_\s].*/, '')}`;
}

function buildUser(resp: {
  localId: string;
  email: string;
  idToken: string;
  refreshToken: string;
  expiresIn: string;
  emailVerified?: boolean;
}): RestAuthUser {
  return {
    uid: resp.localId,
    email: resp.email,
    emailVerified: !!resp.emailVerified,
    idToken: resp.idToken,
    refreshToken: resp.refreshToken,
    expiresAt: Date.now() + Number(resp.expiresIn ?? 3600) * 1000,
  };
}

export const authRest = {
  /** Registro con correo y contraseña. Devuelve el user + tokens. */
  async signUp(email: string, password: string): Promise<RestAuthUser> {
    const resp = await post<any>(`${ID_BASE}:signUp?key=${API_KEY}`, {
      email,
      password,
      returnSecureToken: true,
    });
    return buildUser(resp);
  },

  /** Login con correo y contraseña. */
  async signIn(email: string, password: string): Promise<RestAuthUser> {
    const resp = await post<any>(`${ID_BASE}:signInWithPassword?key=${API_KEY}`, {
      email,
      password,
      returnSecureToken: true,
    });
    return buildUser(resp);
  },

  /** Envía el correo de verificación. */
  async sendEmailVerification(idToken: string): Promise<void> {
    await post(`${ID_BASE}:sendOobCode?key=${API_KEY}`, {
      idToken,
      requestType: 'VERIFY_EMAIL',
    });
  },

  /** Refresca el perfil del user (lee emailVerified actualizado del backend). */
  async lookup(idToken: string): Promise<{ emailVerified: boolean; email: string; localId: string }> {
    const resp = await post<{ users: any[] }>(`${ID_BASE}:lookup?key=${API_KEY}`, { idToken });
    const u = resp.users?.[0];
    return {
      emailVerified: !!u?.emailVerified,
      email: u?.email ?? '',
      localId: u?.localId ?? '',
    };
  },

  /**
   * Login con un IdP federado (Google / Apple / Facebook).
   *
   * Por qué existe: el SDK nativo `signInWithCredential` se cuelga sin
   * timeout cuando la red residencial Wi-Fi tiene packet loss durante el
   * handshake con Identity Toolkit. Mobile data rara vez lo provoca.
   *
   * Esta vía REST hace exactamente lo mismo (entrega el idToken del IdP a
   * Firebase y recibe los tokens del usuario Firebase) pero pasa por
   * `fetchWithRetry`, así que un blip de Wi-Fi se reintenta solo.
   *
   * Args:
   *   - idToken: el ID token que Google/Apple/Facebook le dio al SDK del IdP.
   *   - providerId: 'google.com' | 'apple.com' | 'facebook.com'
   */
  async signInWithIdp(idToken: string, providerId: string): Promise<RestAuthUser> {
    // requestUri puede ser cualquier URL "del cliente" — Firebase lo exige
    // pero no lo valida. Usamos un placeholder local estándar.
    const postBody = `id_token=${encodeURIComponent(idToken)}&providerId=${encodeURIComponent(providerId)}`;
    const resp = await post<any>(`${ID_BASE}:signInWithIdp?key=${API_KEY}`, {
      postBody,
      requestUri: 'http://localhost',
      returnSecureToken: true,
      returnIdpCredential: true,
    });
    // Identity Toolkit devuelve localId/email/idToken/refreshToken/expiresIn
    // + emailVerified=true porque viene de un IdP verificado.
    return buildUser({
      localId: resp.localId,
      email: resp.email ?? '',
      idToken: resp.idToken,
      refreshToken: resp.refreshToken,
      expiresIn: resp.expiresIn,
      emailVerified: resp.emailVerified ?? true,
    });
  },

  /** Renueva el idToken usando el refreshToken. */
  async refresh(refreshToken: string): Promise<{ idToken: string; refreshToken: string; expiresAt: number }> {
    const resp = await fetchWithRetry(`${TOKEN_BASE}?key=${API_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: `grant_type=refresh_token&refresh_token=${encodeURIComponent(refreshToken)}`,
      perAttemptTimeoutMs: REST_PER_ATTEMPT_TIMEOUT_MS,
    });
    if (!resp.ok) {
      throw new Error(`Refresh failed: ${resp.status}`);
    }
    const data = await resp.json();
    return {
      idToken: data.id_token,
      refreshToken: data.refresh_token,
      expiresAt: Date.now() + Number(data.expires_in ?? 3600) * 1000,
    };
  },
};
